import {
  type CustomerInfo,
  type Epik,
  type EpikEvent,
  EpikEventName,
  type EpmsPaymentInfo,
  type EpmsPaymentSourceInfo,
  type EpmsSubscriptionInfo,
  type SubscriptionInfo,
  type TransactionInfo,
} from '@raisenow/epik';
import { Observable } from 'rxjs';

/**
 * Interface that raisenow uses for an error.
 * This is un purpose fairly vague here, as this might
 * be some internal knowledge
 *
 * @example
 * {code: 'NOT_FOUND'}
 */
declare type EpikErrorInfo = Record<string, unknown>;

/**
 * Interface for the data emitted by the `fetch_payment_data_end`
 * event.
 */
interface FetchPaymentData {
  customerError: EpikErrorInfo | null;
  customerInfo: CustomerInfo | null;
  epmsPaymentError: EpikErrorInfo | null;
  epmsPaymentInfo: EpmsPaymentInfo | null;
  epmsPaymentSourceError: EpikErrorInfo | null;
  epmsPaymentSourceInfo: EpmsPaymentSourceInfo | null;
  epmsSubscriptionError: EpikErrorInfo | null;
  epmsSubscriptionInfo: EpmsSubscriptionInfo | null;
  subscriptionError: EpikErrorInfo | null;
  subscriptionInfo: SubscriptionInfo | null;
  transactionError: EpikErrorInfo | null;
  transactionInfo: TransactionInfo | null;
}

export class EpikEventError extends Error {
  override name = 'EpikEventError';

  constructor(
    /**
     * The original error reported by epik
     */
    public readonly epikError: EpikErrorInfo,
    /**
     * Name of the event that the error occured on
     */
    public readonly eventName: EpikEventName,
    message?: string,
  ) {
    super([`[on ${eventName}]`, message].filter((t) => t).join(' '));
  }
}

export function fromEpikEvent(
  epik: Epik,
  eventName: EpikEventName.FETCH_PAYMENT_DATA_END,
): Observable<EpikEvent<FetchPaymentData>>;

export function fromEpikEvent(
  epik: Epik,
  eventName: EpikEventName.FETCH_PAYMENT_DATA_START,
): Observable<EpikEvent<never>>;

/**
 * Creates an `Observable` that emits events of a specific type
 * coming from the given `epik` instance.
 *
 * @param epik EPIK instance
 * @param eventName Name of the EPIK event
 *
 * @returns
 * `Observable` for the given event type
 */
export function fromEpikEvent<T = any>(
  epik: Epik,
  eventName: EpikEventName,
): Observable<EpikEvent<T>> {
  return new Observable((subscriber) => {
    const unsubscribe = epik.events[eventName].subscribe((event) => {
      subscriber.next(event);
    });

    return () => {
      unsubscribe();
    };
  });
}

// For convenience we are also exporting the event names
export { EpikEventName };
